import moment from "moment";

import { NotificationConfigurationCompositeDto } from "@/Common/Api/Services/Dto/NotificationConfigurationDto";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { TimeframeInput } from "@/Common/Components/TimeframeInput/TimeframeInput";
import { NotificationConfigurationInput } from "./NotificationConfiguration.types";
import { useNotificationConfiguration } from "./useNotificationConfiguration";

import "./NotificationConfiguration.css";

interface NotificationConfigurationProps {
    readonly configuration: NotificationConfigurationInput;
    readonly defaultConfiguration: NotificationConfigurationCompositeDto;
    readonly onSubmit?: (form: NotificationConfigurationInput) => void;
    readonly onCancel?: () => void;
    readonly onClose?: () => void;
    readonly title?: string;
}

function NotificationConfiguration(props: NotificationConfigurationProps): JSX.Element {
    const { onSubmit, onClose, configuration, defaultConfiguration, title } = props;
    const config = useNotificationConfiguration({ onSubmit, onClose, configuration, defaultConfiguration });

    function formatTime(time: string): string {
        return moment(`1900-01-01 ${time}`).format("LT").replace(":00", "");
    }

    return (
        <Modal
            onClose={onClose}
            title={`${title}: ${cacheStore.GetString("NotificationConfig_NotificationConfigurationLabel")}`}
            showOverlay
            centerContent
            disableOverlayClose
            footer={(
                <div className="flex-display flex-end">
                    <ButtonGroup spacing="sm">
                        <ButtonControl
                            className="btn btn-default"
                            onClick={config.handleClose}
                            label={cacheStore.GetString("CloseLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={config.handleSave}
                            disabled={config.hasErrors}
                            label={cacheStore.GetString("SaveLabel")}
                        />
                    </ButtonGroup>
                </div>
            )}
        >
            <form ref={config.formRef} onSubmit={config.handleSubmit} className="form" id="notification-configuration-form">
                <div>
                    <h4 style={{ display: "inline" }}>
                        <strong>{cacheStore.GetString("NotificationConfig_PatientCancellationLimitLabel")}</strong>
                    </h4>
                    {config.values.showDefaultConfigurationCheckboxes && (
                        <CheckboxControl
                            checked={config.values.useDefaultCancelConfig}
                            label={cacheStore.GetString("NotificationConfig_UseDefaultConfigurationLabel")}
                            onChange={config.handleUseDefaultCancellationChange}
                            style={{ marginLeft: 15 }}
                        />
                    )}
                </div>

                <CheckboxControl
                    checked={!config.values.enableCancellation}
                    label={cacheStore.GetString("NotificationConfig_BlockCancellationLabel")}
                    onChange={config.handleBlockCancellationChange}
                    disabled={config.values.useDefaultCancelConfig}
                />

                {config.values.enableCancellation && (
                    <TimeframeInput
                        value={config.values.cancellationLimit}
                        error={config.errors.cancellationLimit}
                        onChange={config.handleCancellationChange}
                        name="cancellationLimit"
                        label={cacheStore.GetString("NotificationConfig_BeforeAppointmentLabel")}
                        disabled={config.values.useDefaultCancelConfig}
                    />
                )}

                <hr />
                <h4 style={{ display: "inline" }}>
                    <strong>{cacheStore.GetString("NotificationConfig_AppointmentReminderTimingLabel")}</strong>
                </h4>
                {config.values.showDefaultConfigurationCheckboxes && (
                    <CheckboxControl
                        checked={config.values.useDefaultReminderConfig}
                        label={cacheStore.GetString("NotificationConfig_UseDefaultConfigurationLabel")}
                        onChange={config.handleUseDefaultReminderConfigChange}
                        style={{ marginLeft: 15 }}
                    />
                )}
                <p>
                    {cacheStore.GetString("NotificationConfig_AppointmentReminderTimingNoteLabel", {
                        notificationEndTime: formatTime(config.values.defaultConfiguration.NotificationEndTime),
                        notificationStartTime: formatTime(config.values.defaultConfiguration.NotificationStartTime)
                    })}
                </p>

                <div key="firstReminder">
                    <h5><strong>{cacheStore.GetString("NotificationConfig_FirstReminderLabel")}</strong></h5>
                    <p>{cacheStore.GetString("NotificationConfig_FirstReminderHintLabel")}</p>
                    <TimeframeInput
                        value={config.values.firstReminder}
                        error={config.errors["firstReminder"]}
                        onChange={config.handleRemindersChange}
                        name="firstReminder"
                        label={cacheStore.GetString("NotificationConfig_BeforeAppointmentLabel")}
                        disabled={config.values.useDefaultReminderConfig}
                    />
                </div>

                <div key="secondReminder">
                    <h5><strong>{cacheStore.GetString("NotificationConfig_SecondReminderLabel")}</strong></h5>
                    <p>{cacheStore.GetString("NotificationConfig_SecondReminderHintLabel")}</p>
                    <TimeframeInput
                        value={config.values.secondReminder}
                        error={config.errors["secondReminder"]}
                        onChange={config.handleRemindersChange}
                        name="secondReminder"
                        label={cacheStore.GetString("NotificationConfig_BeforeAppointmentLabel")}
                        disabled={config.values.useDefaultReminderConfig}
                    />
                </div>

                <hr />
                <h4 style={{ display: "inline" }}>
                    <strong>{cacheStore.GetString("NotificationConfig_CustomNotificationContentLabel")}</strong>
                </h4>
                {config.values.showDefaultConfigurationCheckboxes && (
                    <CheckboxControl
                        checked={config.values.useDefaultNotificationContent}
                        label={cacheStore.GetString("NotificationConfig_UseDefaultConfigurationLabel")}
                        onChange={config.handleUseDefaultNotificationContentChange}
                        labelClassName="margin-md-left"
                    />
                )}
                <p>{cacheStore.GetString("NotificationConfig_CustomNotificationContentNoteLabel")}</p>

                <div className="form-group">
                    <label htmlFor="content-fr" className="control-label">
                        {cacheStore.GetString("NotificationConfig_NotificationContentLabel")} ({cacheStore.GetString("FrenchLabelD")})
                    </label>

                    <textarea
                        name="Fr"
                        value={config.values.notificationContent?.Fr ?? ""}
                        id="content-fr"
                        onChange={config.handleContentChange}
                        className="form-control"
                        disabled={config.values.useDefaultNotificationContent}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="content-en" className="control-label">
                        {cacheStore.GetString("NotificationConfig_NotificationContentLabel")} ({cacheStore.GetString("EnglishLabelD")})
                    </label>

                    <textarea
                        name="En"
                        value={config.values.notificationContent?.En ?? ""}
                        id="content-en"
                        onChange={config.handleContentChange}
                        className="form-control"
                        disabled={config.values.useDefaultNotificationContent}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="content-es" className="control-label">
                        {cacheStore.GetString("NotificationConfig_NotificationContentLabel")} ({cacheStore.GetString("SpanishLabelD")})
                    </label>

                    <textarea
                        name="Es"
                        value={config.values.notificationContent?.Es ?? ""}
                        id="content-es"
                        onChange={config.handleContentChange}
                        className="form-control"
                        disabled={config.values.useDefaultNotificationContent}
                    />
                </div>
            </form>
        </Modal>
    );
}

export { NotificationConfiguration, NotificationConfigurationProps };
